/**
 * Comprehensive Logging Utility for CallerID Reputation Monitor
 *
 * This module provides extensive logging capabilities with:
 * - Multiple log levels (DEBUG, INFO, WARN, ERROR, FATAL)
 * - Structured logging with metadata
 * - Performance tracking
 * - Error categorization
 * - Log persistence (localStorage for MVP)
 * - Console formatting with colors
 */

// ============================================================================
// Types and Interfaces
// ============================================================================

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  FATAL = 4,
}

export enum LogCategory {
  AUTH = 'AUTH',
  API = 'API',
  UI = 'UI',
  STATE = 'STATE',
  NAVIGATION = 'NAVIGATION',
  VALIDATION = 'VALIDATION',
  PERFORMANCE = 'PERFORMANCE',
  PAYMENT = 'PAYMENT',
  DATA = 'DATA',
  UPLOAD = 'UPLOAD',
  SYSTEM = 'SYSTEM',
}

export interface LogEntry {
  id: string;
  timestamp: string;
  level: LogLevel;
  levelName: string;
  category: LogCategory;
  message: string;
  context?: string;
  metadata?: Record<string, unknown>;
  error?: {
    name: string;
    message: string;
    stack?: string;
    code?: string;
  };
  userAgent?: string;
  url?: string;
  userId?: string;
  sessionId?: string;
  duration?: number;
}

export interface PerformanceMetric {
  name: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  metadata?: Record<string, unknown>;
}

// ============================================================================
// Configuration
// ============================================================================

const LOG_STORAGE_KEY = 'callerid_logs';
const MAX_STORED_LOGS = 1000;
const LOG_LEVEL_NAMES: Record<LogLevel, string> = {
  [LogLevel.DEBUG]: 'DEBUG',
  [LogLevel.INFO]: 'INFO',
  [LogLevel.WARN]: 'WARN',
  [LogLevel.ERROR]: 'ERROR',
  [LogLevel.FATAL]: 'FATAL',
};

const LOG_LEVEL_COLORS: Record<LogLevel, string> = {
  [LogLevel.DEBUG]: '#6B7280',
  [LogLevel.INFO]: '#3B82F6',
  [LogLevel.WARN]: '#F59E0B',
  [LogLevel.ERROR]: '#EF4444',
  [LogLevel.FATAL]: '#DC2626',
};

const LOG_LEVEL_EMOJI: Record<LogLevel, string> = {
  [LogLevel.DEBUG]: '[DEBUG]',
  [LogLevel.INFO]: '[INFO]',
  [LogLevel.WARN]: '[WARN]',
  [LogLevel.ERROR]: '[ERROR]',
  [LogLevel.FATAL]: '[FATAL]',
};

// ============================================================================
// Utility Functions
// ============================================================================

function generateLogId(): string {
  return `log_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

function generateSessionId(): string {
  let sessionId = sessionStorage.getItem('callerid_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    sessionStorage.setItem('callerid_session_id', sessionId);
  }
  return sessionId;
}

function getConfiguredLogLevel(): LogLevel {
  const envLevel = import.meta.env.VITE_LOG_LEVEL?.toUpperCase();
  switch (envLevel) {
    case 'DEBUG': return LogLevel.DEBUG;
    case 'INFO': return LogLevel.INFO;
    case 'WARN': return LogLevel.WARN;
    case 'ERROR': return LogLevel.ERROR;
    case 'FATAL': return LogLevel.FATAL;
    default: return LogLevel.DEBUG;
  }
}

function isLoggingEnabled(): boolean {
  return import.meta.env.VITE_ENABLE_LOGGING !== 'false';
}

function formatTimestamp(date: Date): string {
  return date.toISOString();
}

function serializeError(error: unknown): LogEntry['error'] | undefined {
  if (!error) return undefined;

  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
      code: (error as Error & { code?: string }).code,
    };
  }

  if (typeof error === 'string') {
    return {
      name: 'StringError',
      message: error,
    };
  }

  return {
    name: 'UnknownError',
    message: JSON.stringify(error),
  };
}

// ============================================================================
// Log Storage (LocalStorage for MVP)
// ============================================================================

function getStoredLogs(): LogEntry[] {
  try {
    const stored = localStorage.getItem(LOG_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('[Logger] Failed to retrieve stored logs:', e);
  }
  return [];
}

function storeLog(entry: LogEntry): void {
  try {
    const logs = getStoredLogs();
    logs.push(entry);

    // Keep only the most recent logs
    const trimmedLogs = logs.slice(-MAX_STORED_LOGS);
    localStorage.setItem(LOG_STORAGE_KEY, JSON.stringify(trimmedLogs));
  } catch (e) {
    console.error('[Logger] Failed to store log:', e);
  }
}

function clearStoredLogs(): void {
  try {
    localStorage.removeItem(LOG_STORAGE_KEY);
    console.info('[Logger] Cleared all stored logs');
  } catch (e) {
    console.error('[Logger] Failed to clear stored logs:', e);
  }
}

// ============================================================================
// Performance Tracking
// ============================================================================

const performanceMetrics: Map<string, PerformanceMetric> = new Map();

function startPerformanceTimer(name: string, metadata?: Record<string, unknown>): void {
  performanceMetrics.set(name, {
    name,
    startTime: performance.now(),
    metadata,
  });

  Logger.debug(
    LogCategory.PERFORMANCE,
    `Performance timer started: ${name}`,
    'PerformanceTracker',
    { timerName: name, ...metadata }
  );
}

function endPerformanceTimer(name: string, additionalMetadata?: Record<string, unknown>): number | null {
  const metric = performanceMetrics.get(name);
  if (!metric) {
    Logger.warn(
      LogCategory.PERFORMANCE,
      `Performance timer not found: ${name}`,
      'PerformanceTracker',
      { timerName: name }
    );
    return null;
  }

  const endTime = performance.now();
  const duration = endTime - metric.startTime;

  metric.endTime = endTime;
  metric.duration = duration;
  metric.metadata = { ...metric.metadata, ...additionalMetadata };

  Logger.info(
    LogCategory.PERFORMANCE,
    `Performance timer ended: ${name} - ${duration.toFixed(2)}ms`,
    'PerformanceTracker',
    {
      timerName: name,
      duration,
      durationFormatted: `${duration.toFixed(2)}ms`,
      ...metric.metadata
    }
  );

  performanceMetrics.delete(name);
  return duration;
}

// ============================================================================
// Main Logger Class
// ============================================================================

class LoggerClass {
  private sessionId: string;
  private userId: string | null = null;
  private configuredLevel: LogLevel;

  constructor() {
    this.sessionId = generateSessionId();
    this.configuredLevel = getConfiguredLogLevel();

    console.info(
      `%c[Logger] Initialized - Session: ${this.sessionId} - Level: ${LOG_LEVEL_NAMES[this.configuredLevel]}`,
      'color: #3B82F6; font-weight: bold;'
    );
  }

  setUserId(userId: string | null): void {
    this.userId = userId;
    this.info(LogCategory.AUTH, `User ID set: ${userId || 'null'}`, 'Logger');
  }

  private shouldLog(level: LogLevel): boolean {
    if (!isLoggingEnabled()) return false;
    return level >= this.configuredLevel;
  }

  private createEntry(
    level: LogLevel,
    category: LogCategory,
    message: string,
    context?: string,
    metadata?: Record<string, unknown>,
    error?: unknown
  ): LogEntry {
    return {
      id: generateLogId(),
      timestamp: formatTimestamp(new Date()),
      level,
      levelName: LOG_LEVEL_NAMES[level],
      category,
      message,
      context,
      metadata,
      error: serializeError(error),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      userId: this.userId || undefined,
      sessionId: this.sessionId,
    };
  }

  private logToConsole(entry: LogEntry): void {
    const color = LOG_LEVEL_COLORS[entry.level];
    const emoji = LOG_LEVEL_EMOJI[entry.level];
    const prefix = `%c${emoji} [${entry.category}]${entry.context ? ` [${entry.context}]` : ''}`;

    const styles = `color: ${color}; font-weight: bold;`;

    const args: unknown[] = [prefix, styles, entry.message];

    if (entry.metadata && Object.keys(entry.metadata).length > 0) {
      args.push('\n  Metadata:', entry.metadata);
    }

    if (entry.error) {
      args.push('\n  Error:', entry.error);
    }

    switch (entry.level) {
      case LogLevel.DEBUG:
        console.debug(...args);
        break;
      case LogLevel.INFO:
        console.info(...args);
        break;
      case LogLevel.WARN:
        console.warn(...args);
        break;
      case LogLevel.ERROR:
      case LogLevel.FATAL:
        console.error(...args);
        break;
    }
  }

  private log(
    level: LogLevel,
    category: LogCategory,
    message: string,
    context?: string,
    metadata?: Record<string, unknown>,
    error?: unknown
  ): LogEntry | null {
    if (!this.shouldLog(level)) return null;

    const entry = this.createEntry(level, category, message, context, metadata, error);

    this.logToConsole(entry);
    storeLog(entry);

    return entry;
  }

  // Public logging methods
  debug(category: LogCategory, message: string, context?: string, metadata?: Record<string, unknown>): LogEntry | null {
    return this.log(LogLevel.DEBUG, category, message, context, metadata);
  }

  info(category: LogCategory, message: string, context?: string, metadata?: Record<string, unknown>): LogEntry | null {
    return this.log(LogLevel.INFO, category, message, context, metadata);
  }

  warn(category: LogCategory, message: string, context?: string, metadata?: Record<string, unknown>, error?: unknown): LogEntry | null {
    return this.log(LogLevel.WARN, category, message, context, metadata, error);
  }

  error(category: LogCategory, message: string, context?: string, metadata?: Record<string, unknown>, error?: unknown): LogEntry | null {
    return this.log(LogLevel.ERROR, category, message, context, metadata, error);
  }

  fatal(category: LogCategory, message: string, context?: string, metadata?: Record<string, unknown>, error?: unknown): LogEntry | null {
    return this.log(LogLevel.FATAL, category, message, context, metadata, error);
  }

  // Utility methods
  getLogs(): LogEntry[] {
    return getStoredLogs();
  }

  clearLogs(): void {
    clearStoredLogs();
  }

  getSessionId(): string {
    return this.sessionId;
  }

  // Performance tracking
  startTimer(name: string, metadata?: Record<string, unknown>): void {
    startPerformanceTimer(name, metadata);
  }

  endTimer(name: string, metadata?: Record<string, unknown>): number | null {
    return endPerformanceTimer(name, metadata);
  }

  // Group logging for related operations
  group(category: LogCategory, groupName: string, context?: string): void {
    if (!this.shouldLog(LogLevel.DEBUG)) return;
    console.group(`%c[${category}]${context ? ` [${context}]` : ''} ${groupName}`, 'color: #6B7280;');
  }

  groupEnd(): void {
    if (!this.shouldLog(LogLevel.DEBUG)) return;
    console.groupEnd();
  }

  // Table logging for data
  table(category: LogCategory, data: unknown[], context?: string): void {
    if (!this.shouldLog(LogLevel.DEBUG)) return;
    console.info(`%c[${category}]${context ? ` [${context}]` : ''} Data Table:`, 'color: #3B82F6;');
    console.table(data);
  }
}

// ============================================================================
// Export Singleton Instance
// ============================================================================

export const Logger = new LoggerClass();

// ============================================================================
// Error Boundary Helper
// ============================================================================

export function logComponentError(
  error: Error,
  componentStack: string,
  componentName?: string
): LogEntry | null {
  return Logger.error(
    LogCategory.UI,
    `Component Error in ${componentName || 'Unknown Component'}`,
    'ErrorBoundary',
    {
      componentName,
      componentStack,
    },
    error
  );
}

// ============================================================================
// API Error Helper
// ============================================================================

export function logApiError(
  endpoint: string,
  method: string,
  status: number | null,
  error: unknown,
  requestData?: unknown
): LogEntry | null {
  return Logger.error(
    LogCategory.API,
    `API Error: ${method} ${endpoint} - Status: ${status || 'N/A'}`,
    'ApiClient',
    {
      endpoint,
      method,
      status,
      requestData: requestData ? JSON.stringify(requestData).substring(0, 500) : undefined,
    },
    error
  );
}

// ============================================================================
// Auth Helper
// ============================================================================

export function logAuthEvent(
  event: 'login' | 'logout' | 'signup' | 'token_refresh' | 'session_expired' | 'oauth_start' | 'oauth_callback' | 'oauth_error',
  provider?: string,
  success?: boolean,
  metadata?: Record<string, unknown>,
  error?: unknown
): LogEntry | null {
  const message = `Auth Event: ${event}${provider ? ` (${provider})` : ''} - ${success ? 'Success' : success === false ? 'Failed' : 'In Progress'}`;

  if (success === false && error) {
    return Logger.error(LogCategory.AUTH, message, 'AuthService', { event, provider, ...metadata }, error);
  }

  return Logger.info(LogCategory.AUTH, message, 'AuthService', { event, provider, success, ...metadata });
}

// ============================================================================
// Navigation Helper
// ============================================================================

export function logNavigation(
  from: string,
  to: string,
  trigger: 'user' | 'redirect' | 'programmatic',
  metadata?: Record<string, unknown>
): LogEntry | null {
  return Logger.info(
    LogCategory.NAVIGATION,
    `Navigation: ${from} -> ${to} (${trigger})`,
    'Router',
    { from, to, trigger, ...metadata }
  );
}

// ============================================================================
// State Change Helper
// ============================================================================

export function logStateChange(
  storeName: string,
  action: string,
  prevState?: unknown,
  nextState?: unknown,
  metadata?: Record<string, unknown>
): LogEntry | null {
  return Logger.debug(
    LogCategory.STATE,
    `State Change: ${storeName}.${action}`,
    storeName,
    {
      action,
      prevState: prevState ? JSON.stringify(prevState).substring(0, 200) : undefined,
      nextState: nextState ? JSON.stringify(nextState).substring(0, 200) : undefined,
      ...metadata,
    }
  );
}

// ============================================================================
// Validation Helper
// ============================================================================

export function logValidationError(
  field: string,
  value: unknown,
  rule: string,
  message: string,
  context?: string
): LogEntry | null {
  return Logger.warn(
    LogCategory.VALIDATION,
    `Validation Failed: ${field} - ${message}`,
    context || 'Validator',
    {
      field,
      value: typeof value === 'string' ? value.substring(0, 100) : value,
      rule,
      message,
    }
  );
}

// ============================================================================
// Payment Helper
// ============================================================================

export function logPaymentEvent(
  event: 'checkout_started' | 'checkout_completed' | 'checkout_failed' | 'payment_method_added' | 'subscription_updated',
  amount?: number,
  currency?: string,
  metadata?: Record<string, unknown>,
  error?: unknown
): LogEntry | null {
  const message = `Payment Event: ${event}${amount ? ` - ${amount} ${currency || 'USD'}` : ''}`;

  if (event === 'checkout_failed' && error) {
    return Logger.error(LogCategory.PAYMENT, message, 'PaymentService', { event, amount, currency, ...metadata }, error);
  }

  return Logger.info(LogCategory.PAYMENT, message, 'PaymentService', { event, amount, currency, ...metadata });
}

// ============================================================================
// Data Operation Helper
// ============================================================================

export function logDataOperation(
  operation: 'fetch' | 'create' | 'update' | 'delete' | 'search' | 'filter' | 'sort' | 'paginate' | 'upload',
  entityType: string,
  entityCount?: number,
  metadata?: Record<string, unknown>,
  error?: unknown
): LogEntry | null {
  const message = `Data Operation: ${operation} ${entityType}${entityCount !== undefined ? ` (${entityCount} items)` : ''}`;

  if (error) {
    return Logger.error(LogCategory.DATA, message, 'DataService', { operation, entityType, entityCount, ...metadata }, error);
  }

  return Logger.info(LogCategory.DATA, message, 'DataService', { operation, entityType, entityCount, ...metadata });
}

